// VerifierVessels page: shows vessels that a Verifier has permission to access
import React, { useState, useEffect, useContext } from 'react';
import { 
  Container, Typography, Table, TableHead, 
  TableRow, TableCell, TableBody, Box, Card, 
  TableContainer, Paper, Button,
  CircularProgress, Alert, Snackbar
} from '@mui/material';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import apiClient from '../api/apiClient';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

export default function VerifierVessels() {
  const { user } = useContext(AuthContext);
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchVessels = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/vessels/verifier'); 
      setVessels(response.data.data || []);
    } catch (err) {
      console.error("Failed to fetch vessels:", err);
      if (err.response && err.response.data) {
        const p = err.response.data;
        setError(p.detail || p.title || 'Failed to load vessels.');
      } else {
        setError('Failed to load vessels.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'VERIFIER') {
      fetchVessels();
    }
  }, [user]);

  return (
    <div className="page-container">
      <Container>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1565c0' }}>
            Your Accessible Vessels
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            These are the vessels you have permission to verify bunkering documents for.
          </Typography>
        </Box>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Card className="card" sx={{ mb: 4 }}>
          <TableContainer component={Paper} className="table-container">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>IMO Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Vessel Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : vessels.length > 0 ? (
                  vessels.map((v) => (
                    <TableRow 
                      key={v.imo}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f9f9f9' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                          <DirectionsBoatIcon sx={{ mr: 1, fontSize: 20, color: '#1565c0' }} />
                          {v.imo}
                        </Typography>
                      </TableCell>
                      <TableCell>{v.name}</TableCell>
                      <TableCell>
                        <Button
                          component={Link}
                          to={`/bunkerings?vessel=${v.imo}`}
                          variant="contained"
                          startIcon={<LocalShippingIcon />}
                          size="small"
                          sx={{
                            bgcolor: '#1565c0',
                            '&:hover': { bgcolor: '#0d47a1' }
                          }}
                        >
                          View Bunkerings
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">
                        You don't have access to any vessels yet. Please contact the shipowner to grant you permission.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    </div>
  );
}